import React, { useState, useEffect, useRef } from 'react';
import { format } from 'date-fns';
import { formatMarkdown } from '@/pages/admin/dashboard/agent/utils/markdownFormatter';
import type { ChatSource } from './chat.types';

interface ChatMessageProps {
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp?: string;
  animate?: boolean; // Enable typing animation
  typingSpeed?: number; // Characters per interval (default: 3)
  sources?: ChatSource[];
  onTypingChange?: (isTyping: boolean) => void;
}

const ChatMessage: React.FC<ChatMessageProps> = ({
  role,
  content,
  timestamp,
  animate = false,
  typingSpeed = 2,
  sources,
  onTypingChange
}) => {
  const isUser = role === 'user';
  const [displayedContent, setDisplayedContent] = useState(animate ? '' : content);
  const [isTyping, setIsTyping] = useState(!!animate);
  const indexRef = useRef(0);

  // 타이핑 애니메이션: animate/content 변경 시 1회 setInterval 실행
  useEffect(() => {
    if (!animate) {
      setDisplayedContent(content);
      setIsTyping(false);
      return;
    }

    indexRef.current = 0;
    setDisplayedContent('');
    setIsTyping(true);

    if (content.length === 0) {
      setIsTyping(false);
      return;
    }

    const interval = setInterval(() => {
      indexRef.current += typingSpeed;
      if (indexRef.current >= content.length) {
        setDisplayedContent(content);
        setIsTyping(false);
        clearInterval(interval);
      } else {
        setDisplayedContent(content.slice(0, indexRef.current));
      }
    }, 20);

    return () => clearInterval(interval);
  }, [animate, content, typingSpeed]);

  // typing 상태 변화를 부모에 통지 (FAQ 컨테이너 노출 제어용)
  useEffect(() => {
    onTypingChange?.(isTyping);
  }, [isTyping, onTypingChange]);


  return (
    <div className={`group/msg flex items-start gap-2.5 ${isUser ? 'flex-row-reverse' : ''}`}>
      {/* Avatar */}
      {!isUser && (
        <div className="w-7 h-7 rounded-full bg-np-100 flex items-center justify-center flex-shrink-0 mt-0.5">
          <svg className="w-4 h-4 text-np-600" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <rect x="4" y="8" width="16" height="10" rx="3" strokeWidth="1.5" />
            <path d="M9 8V5.5M15 8V5.5" strokeWidth="1.5" strokeLinecap="round" />
            <circle cx="9.5" cy="13" r="1.5" strokeWidth="1" />
            <circle cx="14.5" cy="13" r="1.5" strokeWidth="1" />
          </svg>
        </div>
      )}

      {/* Message bubble */}
      <div className={`max-w-[85%] ${isUser ? 'text-right' : ''}`}>
        {isUser ? (
          <div className="rounded-lg rounded-tr-sm bg-np-600 text-white px-3.5 py-2.5 text-sm leading-relaxed">
            <p className="whitespace-pre-wrap">{displayedContent}</p>
          </div>
        ) : (
          <div className="text-sm leading-relaxed text-gray-700 py-1 [&_pre]:!text-[11px] [&_table]:!text-[11px]">
            {formatMarkdown(displayedContent)}
          </div>
        )}

        {/* Source citations */}
        {!isUser && sources && sources.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {sources.map((src, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] bg-gray-50 text-gray-400 rounded-full border border-gray-100"
                title={src.preview || src.title}
              >
                <svg className="w-2.5 h-2.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="truncate max-w-[100px]">{src.title}</span>
              </span>
            ))}
          </div>
        )}

        {/* Timestamp */}
        {timestamp && (
          <div className={`mt-1 text-[10px] text-gray-400 ${isUser ? 'text-right' : ''}`}>
            {format(new Date(timestamp), 'HH:mm')}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatMessage;
